/**
 * CSV Export Modal
 *
 * Modal for exporting activities to CSV with filter options.
 * Hard-limited to 1,000 rows on both client and server.
 */

import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { useExportActivitiesCSVMutation } from '../api/emissions';
import { EmissionScope } from '../types/emissions';

const SCOPE_OPTIONS = [
  { value: '', label: 'All Scopes' },
  { value: EmissionScope.Scope1, label: 'Scope 1' },
  { value: EmissionScope.Scope2, label: 'Scope 2' },
  { value: EmissionScope.Scope3, label: 'Scope 3' },
];

const getCompletedFiscalYear = () => new Date().getFullYear() - 1;

const FISCAL_YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const year = getCompletedFiscalYear() - i;
  return { value: year, label: `FY ${year}` };
});

interface CsvExportModalProps {
  isOpen: boolean;
  toggle: () => void;
  initiativeId: string;
  hasChildren: boolean;
}

export const CsvExportModal = ({ isOpen, toggle, initiativeId, hasChildren }: CsvExportModalProps) => {
  const [selectedYear, setSelectedYear] = useState(getCompletedFiscalYear());
  const [scope, setScope] = useState('');
  const [includeChildren, setIncludeChildren] = useState(hasChildren);
  const [exportError, setExportError] = useState<string | null>(null);

  const [exportCSV, { isLoading }] = useExportActivitiesCSVMutation();

  const handleExport = async () => {
    setExportError(null);
    const result = await exportCSV({
      initiativeId,
      scope: scope || undefined,
      periodStart: `${selectedYear}-01-01`,
      periodEnd: `${selectedYear}-12-31`,
      includeChildren,
    });

    if (result && 'data' in result) {
      const { blob, filename } = result.data as { blob: Blob; filename: string };
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toggle();
    } else {
      setExportError('Failed to export. Please try again.');
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Export Activities to CSV</ModalHeader>
      <ModalBody>
        <p className='text-ThemeTextLight mb-3'>
          Export up to 1,000 activities matching your selected filters.
        </p>

        <div className='mb-3'>
          <label className='form-label fw-medium'>Fiscal Year</label>
          <select
            className='form-select'
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {FISCAL_YEAR_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className='mb-3'>
          <label className='form-label fw-medium'>Scope</label>
          <select
            className='form-select'
            value={scope}
            onChange={(e) => setScope(e.target.value)}
          >
            {SCOPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className='mb-3'>
          <label className='form-label fw-medium d-block mb-2'>Data Scope</label>
          <div className='btn-group w-100'>
            <button
              type='button'
              className={`btn btn-sm ${!includeChildren ? 'btn-success' : 'btn-outline-secondary'}`}
              onClick={() => setIncludeChildren(false)}
            >
              <i className='fa fa-building me-1' />
              Current Level Only
            </button>
            <button
              type='button'
              className={`btn btn-sm ${includeChildren ? 'btn-success' : 'btn-outline-secondary'}`}
              onClick={() => setIncludeChildren(true)}
              disabled={!hasChildren}
            >
              <i className='fa fa-network-wired me-1' />
              Aggregated
            </button>
          </div>
        </div>

        <div className='alert alert-info py-2 mb-0' style={{ fontSize: '0.875rem' }}>
          <i className='fa fa-info-circle me-2' />
          Maximum 1,000 rows exported. Use filters to narrow results.
        </div>

        {exportError && (
          <div className='alert alert-danger py-2 mt-2 mb-0' style={{ fontSize: '0.875rem' }}>
            <i className='fa fa-exclamation-circle me-2' />
            {exportError}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant='secondary' onClick={toggle} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleExport} disabled={isLoading}>
          {isLoading
            ? <><span className='spinner-border spinner-border-sm me-1' />Exporting…</>
            : <><i className='fa fa-download me-1' />Download CSV</>
          }
        </Button>
      </ModalFooter>
    </Modal>
  );
};
